import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Users, RefreshCw, Loader2, LogOut } from "lucide-react";
import { api } from "@/lib/api";

export default function Admin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, n] = await Promise.all([api.get("/contact"), api.get("/newsletter")]);
      setContacts(c.data);
      setSubs(n.data);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const fmt = (d) => new Date(d).toLocaleString();

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    navigate("/login", { replace: true });
  };

  return (
    <div data-testid="admin-page" className="pt-32 md:pt-40 bg-charcoal min-h-screen">
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16 pb-24">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-3">Internal</p>
            <h1 className="font-display text-4xl md:text-5xl text-softwhite">Submissions Admin</h1>
          </div>
          <div className="flex items-center gap-3">
          <button
            onClick={load}
            data-testid="admin-refresh-btn"
            className="inline-flex items-center gap-2 border border-slate/30 hover:border-softwhite text-softwhite px-5 py-3 font-label uppercase tracking-[0.15em] text-xs transition-colors"
          >
            <RefreshCw size={15} /> Refresh
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 border border-crimson/40 hover:bg-crimson text-softwhite px-5 py-3 font-label uppercase tracking-[0.15em] text-xs transition-colors"
          >
            <LogOut size={15} /> Logout
          </button>
          </div>
        </div>

        <div className="mt-10 flex gap-2">
          {[
            { k: "contacts", label: "Contact Inquiries", icon: Mail, count: contacts.length },
            { k: "subs", label: "Newsletter", icon: Users, count: subs.length },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`admin-tab-${t.k}`}
                className={`inline-flex items-center gap-2 px-5 py-3 font-label uppercase tracking-[0.12em] text-xs transition-colors ${
                  tab === t.k ? "bg-crimson text-softwhite" : "bg-surface text-slate hover:text-softwhite"
                }`}
              >
                <Icon size={15} /> {t.label} ({t.count})
              </button>
            );
          })}
        </div>

        <div className="mt-8 border border-slate/20 bg-surface">
          {loading ? (
            <div className="p-16 flex justify-center text-slate">
              <Loader2 className="animate-spin" />
            </div>
          ) : tab === "contacts" ? (
            <div className="divide-y divide-slate/10">
              {contacts.length === 0 && <p className="p-8 text-slate">No inquiries yet.</p>}
              {contacts.map((c) => (
                <div key={c.id} className="p-6 md:p-8" data-testid="admin-contact-row">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <span className="font-display text-xl text-softwhite">{c.name}</span>
                      <span className="px-2 py-0.5 bg-crimson/20 border border-crimson/40 text-crimson-bright font-label uppercase text-[10px] tracking-widest">
                        {c.subject}
                      </span>
                      {c.email_sent && (
                        <span className="text-[10px] font-label uppercase tracking-widest text-slate">emailed ✓</span>
                      )}
                    </div>
                    <span className="text-xs text-slate">{fmt(c.created_at)}</span>
                  </div>
                  <p className="mt-1 text-crimson-bright/80 text-sm">{c.email}</p>
                  <p className="mt-3 text-slate leading-relaxed">{c.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-slate/10">
              {subs.length === 0 && <p className="p-8 text-slate">No subscribers yet.</p>}
              {subs.map((s) => (
                <div key={s.id} className="p-5 md:p-6 flex items-center justify-between" data-testid="admin-sub-row">
                  <span className="text-softwhite">{s.email}</span>
                  <span className="text-xs text-slate">{s.artist} · {fmt(s.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
